import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../../api/client';
import { uploadImage } from '../../api/uploadImage';
import { useToast } from '../../context/ToastContext';
import ToastHtmlEditor from '../../components/ToastHtmlEditor';

const AI_PROVIDER_LABELS = {
  OPENAI: 'OpenAI (ChatGPT)',
  ANTHROPIC: 'Anthropic (Claude)',
  GEMINI: 'Google Gemini',
};

const emptyForm = {
  title: '',
  slug: '',
  summary: '',
  contentHtml: '',
  contentSource: '',
  categoryId: '',
  aiGenerated: false,
};

export default function PostEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const toast = useToast();
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // 변경 감지: 최초 로드 후 사용자가 뭔가 바꿨는지 추적
  const [isDirty, setIsDirty] = useState(false);

  // 미리보기 상태
  const [previewOpen, setPreviewOpen] = useState(false);

  // AI 패널 상태
  const [aiOpen, setAiOpen] = useState(false);
  const [aiProviders, setAiProviders] = useState([]);
  const [aiProvider, setAiProvider] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  // 에디터 초기 콘텐츠 설정 추적
  const contentSetRef = useRef(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  // 편집 모드: 데이터 로드 후 에디터에 콘텐츠 삽입 (한 번만)
  useEffect(() => {
    if (editorRef.current && !loading && !contentSetRef.current) {
      editorRef.current.setHTML(form.contentHtml || '');
      contentSetRef.current = true;
      // 초기 데이터 로드 완료 후 dirty 초기화
      setIsDirty(false);
    }
  }, [loading, form.contentHtml]);

  // 브라우저 이탈 방지 (탭 닫기, 새로고침, URL 직접 입력)
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // 제목 → slug 자동 생성
  const handleTitleChange = (value) => {
    setIsDirty(true);
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: isNew
        ? value.toLowerCase()
            .replace(/[^a-z0-9가-힣\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
        : prev.slug,
    }));
  };

  const markDirty = (updates) => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    api.get('/api/admin/categories').then((res) => setCategories(res.data)).catch(() => {});

    api.get('/api/admin/integrations')
      .then((res) => {
        const enabled = res.data.filter(
          (i) => ['OPENAI', 'ANTHROPIC', 'GEMINI'].includes(i.provider) && i.enabled
        );
        setAiProviders(enabled);
        if (enabled.length > 0) setAiProvider(enabled[0].provider);
      })
      .catch(() => {});

    if (!isNew) {
      api.get(`/api/admin/posts/${id}`)
        .then((res) => {
          const p = res.data;
          setForm({
            title: p.title || '',
            slug: p.slug || '',
            summary: p.summary || '',
            contentHtml: p.contentHtml || '',
            contentSource: p.contentSource || '',
            categoryId: p.category?.id || '',
            aiGenerated: p.aiGenerated || false,
          });
        })
        .catch(() => navigate('/admin/posts'))
        .finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const save = async (publish = false) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug,
        summary: form.summary,
        contentHtml: editorRef.current ? editorRef.current.getHTML() : form.contentHtml,
        contentSource: form.contentSource,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        aiGenerated: form.aiGenerated,
      };

      let post;
      if (isNew) {
        const res = await api.post('/api/admin/posts', payload);
        post = res.data;
      } else {
        const res = await api.put(`/api/admin/posts/${id}`, payload);
        post = res.data;
      }

      // 저장 성공 후 dirty 초기화
      setIsDirty(false);

      if (publish) {
        await api.post(`/api/admin/posts/${post.id}/publish`);
        toast('글이 발행되었습니다.', 'success');
        navigate('/admin/posts');
      } else {
        toast('임시저장 완료!', 'success');
        if (isNew) navigate(`/admin/posts/${post.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast(err.response?.data?.message || '저장 중 오류가 발생했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 취소 버튼 핸들러
  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm('작성 중인 내용이 있습니다. 취소하시겠습니까?')) {
        setIsDirty(false);
        navigate('/admin/posts');
      }
    } else {
      navigate('/admin/posts');
    }
  };

  // ── 이미지 업로드 핸들러 ──

  const handleImageFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      editorRef.current?.insertImage(url);
    } catch {
      toast('이미지 업로드에 실패했습니다.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file);
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        e.preventDefault();
        await handleImageFile(file);
      }
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    for (const item of Array.from(items || [])) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await handleImageFile(file);
      }
    }
  };

  const handleAiGenerate = async () => {
    if (!form.title.trim()) { setAiError('제목을 먼저 입력해 주세요.'); return; }
    if (!aiProvider) { setAiError('활성화된 AI Provider가 없습니다. 설정 페이지에서 API 키를 등록해 주세요.'); return; }
    setAiError('');
    setAiGenerating(true);
    try {
      const res = await api.post('/api/admin/ai/generate-content', {
        title: form.title,
        prompt: aiPrompt || '블로그 글을 작성해주세요.',
        provider: aiProvider,
      });
      const html = res.data.contentHtml;
      setForm((prev) => ({ ...prev, contentHtml: html, aiGenerated: true }));
      editorRef.current?.setHTML(html);
      setIsDirty(true);
      setAiOpen(false);
      toast('AI 초안이 에디터에 채워졌습니다!', 'success');
    } catch (err) {
      setAiError(err.response?.data?.message || 'AI 생성 실패. API 키와 활성화 상태를 확인해 주세요.');
    } finally {
      setAiGenerating(false);
    }
  };

  if (loading) return <div className="loading">불러오는 중...</div>;

  return (
    <div className="post-edit">
      {/* 숨겨진 파일 인풋 (이미지 업로드용) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <div className="edit-header">
        <h2>{isNew ? '새 글 쓰기' : '글 편집'}</h2>
        <div className="edit-actions">
          {uploading && <span className="save-msg">이미지 업로드 중...</span>}
          <button onClick={handleCancel} disabled={saving} className="btn-cancel">
            취소
          </button>
          <button onClick={() => setPreviewOpen(true)} disabled={saving} className="btn-secondary">
            미리보기
          </button>
          <button onClick={() => save(false)} disabled={saving} className="btn-secondary">
            임시저장
          </button>
          <button onClick={() => save(true)} disabled={saving} className="btn-primary">
            발행하기
          </button>
        </div>
      </div>

      <div className="edit-form">
        {/* 제목 */}
        <div className="form-group">
          <input
            className="title-input"
            placeholder="제목을 입력하세요"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
          />
        </div>

        {/* 슬러그 + 카테고리 */}
        <div className="edit-meta">
          <div className="form-group">
            <label>슬러그 (URL)</label>
            <input
              value={form.slug}
              onChange={(e) => markDirty({ slug: e.target.value })}
              placeholder="post-url-slug"
            />
          </div>
          <div className="form-group">
            <label>카테고리</label>
            <select
              value={form.categoryId}
              onChange={(e) => markDirty({ categoryId: e.target.value })}
            >
              <option value="">카테고리 없음</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.parent ? `└ ${c.name}` : c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 요약 */}
        <div className="form-group">
          <label>요약</label>
          <textarea
            className="summary-input"
            placeholder="글 요약 (목록에 표시됩니다)"
            value={form.summary}
            onChange={(e) => markDirty({ summary: e.target.value })}
            rows={3}
          />
        </div>

        {/* AI 작성 여부 */}
        <div className="form-group ai-generated-check">
          <label className="ai-check-label">
            <input
              type="checkbox"
              checked={form.aiGenerated}
              onChange={(e) => markDirty({ aiGenerated: e.target.checked })}
            />
            <span className="ai-check-tag">✦ AI 작성</span>
            <span className="ai-check-desc">이 글은 AI가 작성했거나 AI 도움을 받아 작성되었습니다</span>
          </label>
        </div>

        {/* AI 초안 생성 패널 */}
        <div className="ai-panel">
          <button type="button" className="ai-panel-toggle" onClick={() => setAiOpen((v) => !v)}>
            <span>✦ AI 초안 생성</span>
            <span className="ai-toggle-icon">{aiOpen ? '▲' : '▼'}</span>
          </button>

          {aiOpen && (
            <div className="ai-panel-body">
              {aiProviders.length === 0 ? (
                <p className="ai-no-provider">
                  활성화된 AI Provider가 없습니다.{' '}
                  <a href="/admin/settings" target="_blank" rel="noreferrer">설정 페이지</a>에서
                  API 키를 등록하고 활성화해 주세요.
                </p>
              ) : (
                <>
                  <div className="ai-form-row">
                    <div className="form-group" style={{ flex: '0 0 200px' }}>
                      <label>AI Provider</label>
                      <select value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
                        {aiProviders.map((p) => (
                          <option key={p.provider} value={p.provider}>
                            {AI_PROVIDER_LABELS[p.provider] || p.provider}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>추가 지시사항 (선택)</label>
                      <input
                        type="text"
                        placeholder="예: SEO를 고려해서 작성, 초보자를 위한 쉬운 설명..."
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                      />
                    </div>
                  </div>
                  {aiError && <p className="error-msg" style={{ margin: '4px 0' }}>{aiError}</p>}
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={aiGenerating}
                    className="btn-primary ai-generate-btn"
                  >
                    {aiGenerating ? '⟳ AI가 글을 작성하는 중... (30초~1분 소요)' : '✦ AI로 초안 생성'}
                  </button>
                  <small className="form-hint">
                    생성된 내용은 아래 에디터에 자동으로 채워집니다. 제목이 먼저 입력되어 있어야 합니다.
                  </small>
                </>
              )}
            </div>
          )}
        </div>

        {/* 본문 에디터 (Toast UI) */}
        <div className="form-group editor-group">
          <label>본문</label>
          <div className="tiptap-wrapper" onDrop={handleDrop} onPaste={handlePaste}>
            <div className="toast-toolbar-extra">
              <button type="button" className="btn-secondary" onClick={handleImageUploadClick} disabled={uploading}>
                이미지 삽입
              </button>
            </div>
            <ToastHtmlEditor
              ref={editorRef}
              initialHtml={form.contentHtml}
              onChange={(html) => {
                setIsDirty(true);
                setForm((prev) => ({ ...prev, contentHtml: html }));
              }}
            />
          </div>
          <small className="form-hint">표는 툴바의 테이블 기능을 사용해 작성할 수 있습니다.</small>
        </div>
      </div>

      {/* 미리보기 모달 */}
      {previewOpen && (
        <div
          className="preview-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="preview-modal">
            <div className="preview-modal-bar">
              <span>PREVIEW</span>
              <button className="preview-modal-close" onClick={() => setPreviewOpen(false)}>✕</button>
            </div>
            <div className="preview-modal-content">
              {form.categoryId && categories.find((c) => c.id === Number(form.categoryId)) && (
                <span className="preview-cat">
                  {categories.find((c) => c.id === Number(form.categoryId)).name.toUpperCase()}
                </span>
              )}
              <h1 className="preview-title">{form.title || '(제목 없음)'}</h1>
              {form.summary && <p className="preview-summary">{form.summary}</p>}
              <div
                className="preview-body"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    editorRef.current ? editorRef.current.getHTML() : form.contentHtml || '<p>내용이 없습니다.</p>',
                    { USE_PROFILES: { html: true }, ADD_ATTR: ['target', 'rel'] }
                  ),
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
